import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isDarkMode } = useTheme();

  return (
    <div className="app">
      <div className="container" style={{ maxWidth: '800px', padding: '20px' }}>
        {/* Header with Back Button */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '32px',
            paddingTop: '20px'
          }}
        >
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: 'var(--text-primary)',
              fontSize: '16px',
              fontWeight: '500',
              padding: '8px',
              marginLeft: '-8px',
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          >
            <ArrowLeft size={20} />
            Back
          </button>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{
            color: 'var(--text-primary)',
            lineHeight: '1.7'
          }}
        >
          <h1 style={{
            fontSize: '32px',
            fontWeight: '700',
            marginBottom: '8px',
            color: 'var(--text-primary)'
          }}>
            Privacy Policy
          </h1>
          
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '14px',
            marginBottom: '32px'
          }}>
            Last Updated: December 30, 2025
          </p>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              1. Introduction
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              Welcome to miiaitor ("we," "our," or "us"). We are committed to protecting your privacy and ensuring 
              the security of your personal information. This Privacy Policy explains how we collect, use, disclose, 
              and safeguard your information when you use our location-based tour generation service.
            </p>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              By using miiaitor, you agree to the collection and use of information in accordance with this policy.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              2. Information We Collect
            </h2>
            
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: 'var(--text-primary)' }}>
              2.1 Personal Information
            </h3>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              When you create an account using Google authentication, we collect:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>Full name</li>
              <li style={{ marginBottom: '8px' }}>Email address</li>
              <li style={{ marginBottom: '8px' }}>Google profile picture (avatar)</li>
              <li style={{ marginBottom: '8px' }}>Google account ID</li>
            </ul>

            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: 'var(--text-primary)' }}>
              2.2 Location Data
            </h3>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              To provide personalized tour recommendations, we collect:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>Real-time location (GPS coordinates)</li>
              <li style={{ marginBottom: '8px' }}>Location history for tour generation</li>
              <li style={{ marginBottom: '8px' }}>Preferred locations and saved places</li>
            </ul>

            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: 'var(--text-primary)' }}>
              2.3 Usage Data
            </h3>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              We automatically collect information about how you use the Service:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>Tour preferences and selected interests</li>
              <li style={{ marginBottom: '8px' }}>Generated tours and tour history</li>
              <li style={{ marginBottom: '8px' }}>Places visited and saved</li>
              <li style={{ marginBottom: '8px' }}>Selected places for tour customization (optional)</li>
              <li style={{ marginBottom: '8px' }}>Distance unit preferences (miles/kilometers)</li>
              <li style={{ marginBottom: '8px' }}>App usage patterns and interaction data</li>
              <li style={{ marginBottom: '8px' }}>Tour generation time and completion status</li>
            </ul>

            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: 'var(--text-primary)' }}>
              2.4 Device Information
            </h3>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              We collect device-specific information including:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>Device ID (for anonymous browsing and credit tracking)</li>
              <li style={{ marginBottom: '8px' }}>Browser type and version</li>
              <li style={{ marginBottom: '8px' }}>Operating system</li>
              <li style={{ marginBottom: '8px' }}>IP address</li>
              <li style={{ marginBottom: '8px' }}>Device settings and preferences</li>
            </ul>

            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: 'var(--text-primary)' }}>
              2.5 Payment Information
            </h3>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              Payment processing is handled by Paystack. We do not store your credit card information. 
              We collect and store:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>Transaction history</li>
              <li style={{ marginBottom: '8px' }}>Credit balance and usage</li>
              <li style={{ marginBottom: '8px' }}>Payment plan subscriptions</li>
              <li style={{ marginBottom: '8px' }}>Transaction references</li>
            </ul>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              3. How We Use Your Information
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              We use the collected information for the following purposes:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>
                <strong>Service Delivery:</strong> Generate personalized tours based on your location and interests, 
                including AI research, fact-finding with citations, and audio narration (typically 1-3 minutes for tour 
                generation, 2-3 minutes for audio)
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Community Sharing:</strong> Make your generated tours available to other users, building a 
                shared library of local knowledge
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Account Management:</strong> Create and manage your user account
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Authentication:</strong> Verify your identity through Google Sign-In
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Payment Processing:</strong> Process credit purchases and manage your credit balance
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Personalization:</strong> Remember your preferences and customize your experience
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Analytics:</strong> Understand usage patterns and improve the Service
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Communication:</strong> Send service-related notifications and updates
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Security:</strong> Detect and prevent fraud, abuse, and security incidents
              </li>
            </ul>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              4. Data Sharing and Disclosure
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              We do not sell your personal information. We may share your information in the following circumstances:
            </p>

            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: 'var(--text-primary)' }}>
              4.1 Service Providers
            </h3>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              We share information with trusted third-party service providers:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>
                <strong>Google:</strong> For authentication and Google Maps services
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Paystack:</strong> For payment processing
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Supabase:</strong> For database and authentication services
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>PostHog:</strong> For analytics and product insights
              </li>
            </ul>

            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: 'var(--text-primary)' }}>
              4.2 Legal Requirements
            </h3>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              We may disclose your information if required by law or in response to valid requests by public authorities, 
              such as a court or government agency.
            </p>

            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: 'var(--text-primary)' }}>
              4.3 Business Transfers
            </h3>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              In the event of a merger, acquisition, or sale of assets, your information may be transferred to the 
              new owner. We will notify you of any such change.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              5. Data Security
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              We implement industry-standard security measures to protect your information:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>Encryption of data in transit and at rest</li>
              <li style={{ marginBottom: '8px' }}>Row Level Security (RLS) policies on our database</li>
              <li style={{ marginBottom: '8px' }}>Secure authentication through Google OAuth</li>
              <li style={{ marginBottom: '8px' }}>Regular security audits and updates</li>
              <li style={{ marginBottom: '8px' }}>Access controls and monitoring</li>
            </ul>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              However, no method of transmission over the Internet or electronic storage is 100% secure. 
              While we strive to protect your personal information, we cannot guarantee absolute security.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              6. Data Retention
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              We retain your information for as long as necessary to provide the Service and fulfill the purposes 
              outlined in this Privacy Policy. Specifically:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>
                <strong>Account Data:</strong> Retained while your account is active
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Tour History:</strong> Retained to allow you to access past tours
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Transaction Records:</strong> Retained for accounting and legal compliance
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Analytics Data:</strong> Anonymized and aggregated for service improvement
              </li>
            </ul>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              7. Your Rights and Choices
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              You have the following rights regarding your personal information:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>
                <strong>Access:</strong> Request a copy of your personal data
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Correction:</strong> Update or correct inaccurate information through your account settings
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Deletion:</strong> Request deletion of your account and associated data
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Location Control:</strong> Disable location services through your device settings
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Opt-out:</strong> Opt out of analytics tracking (through your account settings)
              </li>
            </ul>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              To exercise these rights, please contact us through the app or our support channels.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              8. Location Data and Permissions
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              Location data is essential to our Service. We use your location to:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>Find places of interest near you</li>
              <li style={{ marginBottom: '8px' }}>Generate location-based tour routes</li>
              <li style={{ marginBottom: '8px' }}>Calculate distances and travel times</li>
              <li style={{ marginBottom: '8px' }}>Show you relevant community tours</li>
            </ul>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              You can control location permissions through your device settings. Note that disabling location services 
              will limit the functionality of the app.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              9. Community Tours and Public Content
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              Tours you generate become part of our community library:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>
                <strong>Public Availability:</strong> Tours you create are visible to all users and can be viewed, 
                saved, and shared by others
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Tour Content:</strong> Tours include the location, selected interests, chosen places (if any), 
                AI-generated narration, and research citations
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Attribution:</strong> Tours are not linked to your personal identity or email address in the 
                public view
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Deletion:</strong> You can delete tours you created, which removes them from the community 
                library and your collection
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Research Sources:</strong> All factual claims include citations with links to publicly 
                available sources
              </li>
            </ul>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              10. Anonymous Browsing
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              You can browse community tours without creating an account. When browsing anonymously:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>We show you 3 nearby tours from the community library</li>
              <li style={{ marginBottom: '8px' }}>We assign you a temporary device ID</li>
              <li style={{ marginBottom: '8px' }}>Your browsing history is not linked to a permanent account</li>
              <li style={{ marginBottom: '8px' }}>Location data is used only for session-based recommendations</li>
              <li style={{ marginBottom: '8px' }}>Limited features are available compared to registered users (creating tours, saving tours, and purchasing credits require sign-in)</li>
            </ul>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              11. Children's Privacy
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              Our Service is not intended for children under the age of 13. We do not knowingly collect personal 
              information from children under 13. If you are a parent or guardian and believe your child has provided 
              us with personal information, please contact us, and we will delete such information.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              12. International Data Transfers
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              Your information may be transferred to and processed in countries other than your country of residence. 
              These countries may have different data protection laws. By using the Service, you consent to the transfer 
              of your information to these countries.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              13. Cookies and Tracking Technologies
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              We use cookies and similar tracking technologies to:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>Maintain your session and authentication state</li>
              <li style={{ marginBottom: '8px' }}>Remember your preferences (theme, distance units)</li>
              <li style={{ marginBottom: '8px' }}>Analyze usage patterns through analytics tools</li>
              <li style={{ marginBottom: '8px' }}>Improve performance and user experience</li>
            </ul>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              You can control cookies through your browser settings, but this may affect functionality.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              14. Changes to This Privacy Policy
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              We may update this Privacy Policy from time to time. We will notify you of any changes by:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>Updating the "Last Updated" date at the top of this policy</li>
              <li style={{ marginBottom: '8px' }}>Posting the new Privacy Policy on this page</li>
              <li style={{ marginBottom: '8px' }}>Sending you an email notification (for significant changes)</li>
            </ul>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              Your continued use of the Service after any changes constitutes acceptance of the updated Privacy Policy.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              15. Contact Us
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, 
              please contact us through:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>The in-app support feature</li>
              <li style={{ marginBottom: '8px' }}>Your account settings</li>
              <li style={{ marginBottom: '8px' }}>Our designated support channels</li>
            </ul>
          </section>

          <section style={{ marginBottom: '48px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              16. Your Consent
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              By using miiaitor, you acknowledge that you have read and understood this Privacy Policy and agree to 
              the collection, use, and disclosure of your information as described herein.
            </p>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
